import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { AuthError } from '@supabase/supabase-js';
import AuthLayout from '../../components/AuthLayout';
import { validateNewPassword, PASSWORD_HELP_TEXT } from '../../utils/passwordValidation';
import {
  ResetPasswordState,
  DEFAULT_STATE,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  SESSION_WAIT_MS
} from './config';

const ResetPassword: React.FC = () => {
  const [state, setState] = useState<ResetPasswordState>(DEFAULT_STATE);
  const supabase = useSupabaseClient();
  const session = useSession();
  const navigate = useNavigate();

  // The recovery link signs the user in via the URL fragment. Wait briefly for
  // that session to be picked up; if none appears the link is invalid/expired
  // (or the account is deactivated, which blocks token issuance).
  useEffect(() => {
    if (session) {
      // Also rescue from 'invalid': on slow connections the session can
      // arrive after the timeout below has already given up.
      setState(prev => (
        prev.step === 'verifying' || prev.step === 'invalid'
          ? { ...prev, step: 'form' }
          : prev
      ));
      return;
    }
    const timer = setTimeout(() => {
      setState(prev => (prev.step === 'verifying' ? { ...prev, step: 'invalid' } : prev));
    }, SESSION_WAIT_MS);
    return () => clearTimeout(timer);
  }, [session]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateNewPassword(state.password, state.confirmPassword);
    if (validationError) {
      setState(prev => ({ ...prev, error: validationError }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { error } = await supabase.auth.updateUser({ password: state.password });
      if (error) {
        throw error;
      }

      setState(prev => ({ ...prev, step: 'complete', loading: false }));
      setTimeout(() => {
        navigate('/projects');
      }, 2000);
    } catch (err) {
      const authError = err as AuthError;
      console.error('Password update error:', authError);
      setState(prev => ({
        ...prev,
        loading: false,
        error: authError.message || ERROR_MESSAGES.UPDATE_FAILED
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value, error: null }));
  };

  const renderContent = () => {
    if (state.step === 'verifying') {
      return (
        <div className="auth-card__spinner">
          <div className="spinner"></div>
        </div>
      );
    }

    if (state.step === 'invalid') {
      return (
        <>
          <h1 className="auth-card__title">Set new password</h1>
          <div className="auth-form">
            <div className="error-message">
              {ERROR_MESSAGES.INVALID_LINK}
            </div>
          </div>
          <div className="auth-card__footer">
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/forgot-password')}
            >
              Request a new reset link
            </button>
          </div>
        </>
      );
    }

    if (state.step === 'complete') {
      return (
        <div className="auth-card__status">
          <div className="auth-card__status-icon">✓</div>
          <h2>{SUCCESS_MESSAGES.PASSWORD_UPDATED}</h2>
          <p>{SUCCESS_MESSAGES.REDIRECT_MESSAGE}</p>
        </div>
      );
    }

    return (
      <>
        <h1 className="auth-card__title">Set new password</h1>
        <p className="auth-card__subtitle">Choose a new password for your account.</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={state.password}
              onChange={handleInputChange}
              placeholder="Your new password"
              disabled={state.loading}
              autoComplete="new-password"
            />
            <div className="password-requirements">
              <small>{PASSWORD_HELP_TEXT}</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={state.confirmPassword}
              onChange={handleInputChange}
              placeholder="Repeat your new password"
              disabled={state.loading}
              autoComplete="new-password"
            />
          </div>

          {state.error && (
            <div className="error-message">
              {state.error}
            </div>
          )}

          <button
            type="submit"
            className="auth-submit"
            disabled={state.loading}
          >
            {state.loading ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </>
    );
  };

  return <AuthLayout>{renderContent()}</AuthLayout>;
};

export default ResetPassword;
